import React, {Fragment, useEffect, useRef, useState} from "react";
import {plural, t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import ApiService from "../../../services/ApiService";
import useSerializedRefresh from "../useSerializedRefresh";
import {goalOf, goalProgress, initials} from "./model";
import styles from "./v2.module.css";

const TEAMS_PER_PAGE = 6
const DONORS_PER_PAGE = 30

function relativeTime(date, langValue, now = Date.now()) {
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ""
    const seconds = Math.round((d.getTime() - now) / 1000)
    const rtf = new Intl.RelativeTimeFormat(langValue || "fr", {numeric: "auto"})
    const abs = Math.abs(seconds)
    if (abs < 60) return rtf.format(0, "second")
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute")
    if (abs < 86400) return rtf.format(Math.round(seconds / 3600), "hour")
    if (abs < 86400 * 30) return rtf.format(Math.round(seconds / 86400), "day")
    return new Intl.DateTimeFormat(langValue || "fr", {day: "numeric", month: "long", year: "numeric"}).format(d)
}

/**
 * Équipes classées selon l'objectif, avec recherche. Les équipes qui n'ont encore
 * reçu aucun don sont repliées derrière un bouton : elles n'encombrent pas le
 * classement, mais restent trouvables (la recherche les montre toujours).
 */
export function Teams({context, campagneData, liveKey, formatMoney, formatNumber, baseUrl}) {
    const [search, setSearch] = useState("")
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [active, setActive] = useState(0)
    const [showEmpty, setShowEmpty] = useState(false)
    const [page, setPage] = useState(1)
    const [loaded, setLoaded] = useState(false)
    const requestRef = useRef(0)

    const goal = goalOf(campagneData)
    const visibleCount = (campagneData?.teams || []).filter(team => !team.hidden).length

    const load = async (nextPage, append) => {
        const request = ++requestRef.current
        const params = new URLSearchParams({page: String(nextPage), limit: String(TEAMS_PER_PAGE)})
        if (search.trim()) params.set("search", search.trim())
        const res = await ApiService.get("/campagne/v2/" + campagneData.id + "/teams?" + params.toString())
        if (request !== requestRef.current || !res?.success) return
        setItems(previous => (append ? [...previous, ...res.data] : res.data))
        setTotal(res.total)
        setActive(Number.isFinite(res.active) ? res.active : res.total)
        setPage(nextPage)
        setLoaded(true)
    }

    useEffect(() => {
        if (visibleCount === 0) return
        const timer = setTimeout(() => load(1, false), search ? 250 : 0)
        return () => clearTimeout(timer)
    }, [search, campagneData?.id, visibleCount])

    // Rafraîchit la première page avec la révision live (sans perdre les pages chargées).
    useSerializedRefresh(async () => {
        if (!loaded || page > 1) return
        await load(1, false)
    }, liveKey)

    if (visibleCount === 0) return null

    const unit = (n) => (n > 1 ? goal.unitLabel?.other : goal.unitLabel?.one) || ""

    const hasGifts = (team) => team.progress.donations > 0 || team.progress.amount > 0
    // Rien à replier pendant une recherche, ni quand aucune équipe n'a encore de don.
    const collapse = !showEmpty && !search.trim() && active > 0
    const visible = collapse ? items.filter(hasGifts) : items
    const emptyCount = total - active
    const canLoadMore = collapse ? visible.length < active : items.length < total

    return (
        <section className={styles.block} aria-labelledby="cv-teams-title">
            <div className={styles.blockInner}>
                <div className={styles.blockHead}>
                    <h2 id="cv-teams-title" className={styles.blockTitle}><Trans>Les équipes</Trans></h2>
                    <span className={styles.count}>{plural(visibleCount, {one: "# équipe", other: "# équipes"})}</span>
                </div>

                {visibleCount > TEAMS_PER_PAGE && (
                    <div className={styles.teamSearch}>
                        <label className={styles.srOnly} htmlFor="cv-team-search">{t`Rechercher une équipe`}</label>
                        <input
                            id="cv-team-search"
                            className={styles.input}
                            type="search"
                            placeholder={t`Rechercher une équipe`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                )}

                {loaded && items.length === 0 && (
                    <p className={styles.empty}><Trans>Aucune équipe ne correspond à cette recherche.</Trans></p>
                )}

                <ol className={styles.teamList}>
                    {visible.map(team => {
                        const progress = team.progress
                        const percent = Math.min(100, Math.max(0, progress.percent))
                        const amountText = goal.mode === "UNITS"
                            ? `${formatNumber(progress.value, 1)} ${unit(progress.value)}`.trim()
                            : goal.mode === "COUNT" ? plural(progress.donations, {one: "# don", other: "# dons"}) : formatMoney(progress.amount)
                        const targetText = goal.mode === "UNITS"
                            ? `${formatNumber(progress.target, 1)} ${unit(progress.target)}`.trim()
                            : goal.mode === "COUNT" ? formatNumber(progress.target, 0) : formatMoney(progress.target)
                        return (
                            <li key={team.id || team.param} className={styles.team}>
                                <span className={styles.rank} aria-hidden="true">{team.rank}</span>
                                <div className={styles.teamTop}>
                                    <a className={styles.teamName} href={baseUrl + "/" + encodeURIComponent(team.param)}>
                                        <span className={styles.srOnly}>{t`Rang ${team.rank} :`} </span><span dir="auto">{team.label}</span>
                                    </a>
                                    <span className={styles.teamAmount}>{amountText}</span>
                                </div>
                                {progress.target > 0 && (
                                    <div
                                        className={styles.teamTrack}
                                        role="progressbar"
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={Math.round(percent)}
                                        aria-label={t`${team.label} : ${formatNumber(progress.percent, 0)} % de son objectif`}
                                    >
                                        <div className={styles.trackFill} style={{width: percent + "%"}}/>
                                    </div>
                                )}
                                <span className={styles.teamMeta}>
                                    {progress.target > 0
                                        ? t`${formatNumber(progress.percent, 0)} % de ${targetText}`
                                        : plural(progress.donations, {one: "# don", other: "# dons"})}
                                </span>
                            </li>
                        )
                    })}
                </ol>

                {canLoadMore && (
                    <button type="button" className={styles.secondary} onClick={() => load(page + 1, true)}>
                        <Trans>Voir plus d'équipes</Trans>
                    </button>
                )}

                {collapse && !canLoadMore && emptyCount > 0 && (
                    <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => {
                            setShowEmpty(true)
                            if (items.length < total) load(page + 1, true)
                        }}
                    >
                        {emptyCount > 1
                            ? <Trans>Afficher les {emptyCount} équipes qui attendent leur premier don</Trans>
                            : <Trans>Afficher l'équipe qui attend son premier don</Trans>}
                    </button>
                )}
            </div>
        </section>
    )
}

/**
 * Derniers dons : nom affiché (ou « Anonyme »), montant, date lisible, équipe, et
 * dédicace distinguée du nom.
 */
export function Donors({context, campagneData, liveKey, team, formatMoney}) {
    const [items, setItems] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const config = campagneData?.config || {}
    const teams = campagneData?.teams || []

    const url = (p) => "/campagne/donors/" + campagneData.id + "?page=" + p + (team ? "&team=" + encodeURIComponent(team) : "")

    const keyOf = (d) => String(d?._id || d?.id || "")

    const loadFirst = async () => {
        const res = await ApiService.get(url(1))
        if (!Array.isArray(res?.data)) return
        setItems(previous => {
            const seen = new Set(res.data.map(keyOf))
            return [...res.data, ...previous.filter(d => !seen.has(keyOf(d)))]
        })
        setCount(res.stats?.count?.total || 0)
        if (!loaded) setHasMore(res.data.length >= DONORS_PER_PAGE)
        setLoaded(true)
    }

    const loadMore = async () => {
        const next = page + 1
        const res = await ApiService.get(url(next))
        if (!Array.isArray(res?.data)) return
        setItems(previous => {
            const seen = new Set(previous.map(keyOf))
            return [...previous, ...res.data.filter(d => !seen.has(keyOf(d)))]
        })
        setPage(next)
        setHasMore(res.data.length >= DONORS_PER_PAGE)
    }

    useSerializedRefresh(loadFirst, liveKey)

    if (!loaded) return null

    return (
        <section className={`${styles.block} ${styles.blockSoft}`} aria-labelledby="cv-donors-title">
            <div className={styles.blockInner}>
                <div className={styles.blockHead}>
                    <h2 id="cv-donors-title" className={styles.blockTitle}><Trans>Derniers dons</Trans></h2>
                    {count > 0 && <span className={styles.count}>{plural(count, {one: "# don", other: "# dons"})}</span>}
                </div>

                {items.length === 0 && (
                    <p className={styles.empty}><Trans>Soyez le premier à soutenir cette campagne.</Trans></p>
                )}

                <ul className={styles.donorList}>
                    {items.map(d => {
                        const extra = d.campagneExtraData || {}
                        const name = (extra.displayName || "").trim() || t`Anonyme`
                        const donorTeam = teams.find(item => item.param === extra.team && !item.hidden)
                        const monthly = d.type === "ABONNEMENT" && d.montantPerMonth && (config.productMonth || config.monthlyAmountDisplay)
                        const amount = monthly ? t`${formatMoney(d.montantPerMonth, d.devise)} /mois` : formatMoney(d.montant, d.devise)
                        // « Masquer la date des dons » de l'ancienne page.
                        const when = config.hideDonDate === true ? null : relativeTime(d.datetime, context.langValue)
                        return (
                            <li key={keyOf(d)} className={styles.donor}>
                                <span className={styles.avatar} aria-hidden="true">{initials(name)}</span>
                                <div className={styles.donorBody}>
                                    <div className={styles.donorTop}>
                                        <span className={styles.donorName} dir="auto">{name}</span>
                                        <span className={styles.donorAmount}>{amount}</span>
                                    </div>
                                    <div className={styles.donorMeta}>
                                        {[when, donorTeam ? t`pour ${donorTeam.label}` : null, d.type === "DETTE" ? t`Promesse` : null].filter(Boolean).join(" · ")}
                                    </div>
                                    {d.commentaire && <p className={styles.dedication} dir="auto">{d.commentaire}</p>}
                                </div>
                            </li>
                        )
                    })}
                </ul>

                {hasMore && (
                    <button type="button" className={styles.secondary} onClick={loadMore}>
                        <Trans>Voir plus de dons</Trans>
                    </button>
                )}
            </div>
        </section>
    )
}
